import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import type { SurveyQuestion } from '@/types';
import { cn } from '@/lib/utils';

/** BANNER question metadata: display-only message with optional primary/secondary CTAs */
interface BannerMetadata {
  displayOnly?: boolean;
  ctaPrimary?: string;
  ctaSecondary?: string;
  ctaLeadsTo?: string;
}

interface BannerCardProps {
  question: SurveyQuestion;
  onContinue: () => void;
  /** Optional handler for secondary CTA (e.g. "I already have an account" → auth) */
  onSecondaryClick?: () => void;
}

// Extract emoji from text (if present at the start)
const extractEmoji = (text: string): { emoji: string | null; cleanText: string } => {
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\u{1F3FB}-\u{1F3FF}|\u{1F9B0}-\u{1F9B3}|\u{200D}|\u{FE0F})+/u;
  const match = text.match(emojiRegex);
  
  if (match && match[0]) {
    return {
      emoji: match[0].trim(),
      cleanText: text.replace(emojiRegex, '').trim(),
    };
  }
  
  return { emoji: null, cleanText: text };
};

// Positive tone emojis for banners
const defaultPositiveEmojis = ['🎉', '✨', '🚀', '💪', '🌟', '🎯', '💡', '🔥'];

export const BannerCard = ({ question, onContinue, onSecondaryClick }: BannerCardProps) => {
  const metadata = (question.metadata ?? {}) as BannerMetadata;
  const primaryLabel = metadata.ctaPrimary ?? 'Continue';
  const secondaryLabel = metadata.ctaSecondary;
  const hasSecondary = Boolean(secondaryLabel && onSecondaryClick);

  // Extract emoji from question text or heading
  const questionText = question.text || question.heading?.heading || '';
  const headingText = question.heading?.heading || '';
  let { emoji, cleanText } = extractEmoji(questionText);

  if (!emoji && headingText && headingText !== questionText) {
    const headingResult = extractEmoji(headingText);
    if (headingResult.emoji) {
      emoji = headingResult.emoji;
      cleanText = headingResult.cleanText || questionText;
    }
  }

  // Use default positive emoji if none found
  const displayEmoji = emoji || defaultPositiveEmojis[Math.floor(Math.random() * defaultPositiveEmojis.length)];
  const displayText = cleanText || questionText || headingText || '';

  // Prevent body scroll when overlay is shown
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop with blur - positive gradient */}
      <div 
        className={cn(
          'absolute inset-0 bg-gradient-to-br',
          'from-emerald-500/20 via-primary/10 to-amber-500/5',
          'backdrop-blur-md'
        )}
        onClick={onContinue}
        aria-hidden="true"
      />
      
      {/* Content Card */}
      <div className="relative w-full max-w-2xl mx-auto animate-scale-in">
        <div className={cn(
          'relative rounded-3xl border border-border/50 bg-card/95 backdrop-blur-xl',
          'shadow-2xl overflow-hidden',
          'p-8 sm:p-12 space-y-8'
        )}>
          {/* Decorative gradient overlays - positive tone */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(251,191,36,0.1),transparent_50%)]" />
          
          {/* Content */}
          <div className="relative space-y-6 text-center">
            {/* Emoji Display - Large and prominent */}
            <div className={cn(
              'inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-3xl',
              'bg-gradient-to-br from-emerald-500/20 to-amber-500/10',
              'border-2 border-emerald-400/30 shadow-xl',
              'animate-bounce-slow'
            )}>
              <span 
                className="text-5xl sm:text-6xl leading-none select-none"
                role="img"
                aria-hidden="true"
              >
                {displayEmoji}
              </span>
            </div>

            {/* Section Badge - if section exists */}
            {question.section?.heading && (
              <div className="inline-flex items-center gap-2">
                <span className={cn(
                  'px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider',
                  'border backdrop-blur-sm',
                  'bg-emerald-500/20 text-emerald-foreground border-emerald-400/30'
                )}>
                  {question.section.heading}
                </span>
              </div>
            )}

            {/* Heading - if present (optional; body message can be the main content) */}
            {question.heading?.heading && question.heading.heading !== question.text && (
              <h2 className={cn(
                'text-2xl sm:text-3xl font-bold leading-tight text-foreground'
              )}>
                {question.heading.heading}
              </h2>
            )}

            {/* Body: banner message (question.text) - displayOnly, no user input */}
            {displayText && (
              <div className="space-y-2">
                <p className={cn(
                  'text-xl sm:text-2xl font-medium text-foreground leading-snug max-w-lg mx-auto'
                )}>
                  {displayText}
                </p>
                {question.helpText && (
                  <p className="text-base text-muted-foreground max-w-md mx-auto">
                    {question.helpText}
                  </p>
                )}
              </div>
            )}

            {/* Subheading - if exists */}
            {question.heading?.shortHeading && (
              <p className="text-base text-muted-foreground/80">
                {question.heading.shortHeading}
              </p>
            )}

            {/* CTA buttons from metadata: ctaPrimary (e.g. "Get Started"), optional ctaSecondary (e.g. "I already have an account") */}
            <div className={cn(
              'pt-8 flex flex-col sm:flex-row items-center justify-center gap-3',
              hasSecondary && 'sm:gap-4'
            )}>
              <Button
                variant="default"
                size="lg"
                onClick={onContinue}
                className={cn(
                  'min-w-[160px] px-8 py-6 text-base font-semibold',
                  'shadow-lg hover:shadow-xl transition-all',
                  'bg-gradient-to-r from-emerald-500 to-primary hover:from-emerald-600 hover:to-primary/90'
                )}
              >
                {primaryLabel}
              </Button>
              {hasSecondary && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onSecondaryClick}
                  className="min-w-[160px] px-8 py-6 text-base font-medium border-2"
                >
                  {secondaryLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};